(() => {
  const SELECTOR = 'dawn-product-upsell';

  const parseBoolean = (value) => value === true || value === 'true';

  const formatMoney = (cents, format) => {
    const value = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    const moneyFormat = format || '${{amount}}';

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const normalized = Number(number);
      if (!Number.isFinite(normalized)) return '0';

      const parts = (normalized / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };

    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(value);

    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount':
      default:
        formatted = formatWithDelimiters(value, 2);
        break;
    }

    return moneyFormat.replace(match[0], formatted);
  };

  class DawnProductUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.duInitialized === 'true') return;
      this.dataset.duInitialized = 'true';
      this.initializeCards();
    }

    disconnectedCallback() {
      // Shopify's theme editor can replace this product section at any time.
      // The dialog is moved to body, so clean it up if its owner disappears.
      if (this._modalCloseTimer) clearTimeout(this._modalCloseTimer);
      if (this.modal) {
        this.modal.classList.remove('is-open');
        if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
        this.modal.hidden = true;
        this.modal.remove();
      }
      this.unlockPageScroll();
      if (this._onModalKeydown) document.removeEventListener('keydown', this._onModalKeydown);
    }

    initializeCards() {
      const cards = [...this.querySelectorAll('[data-upsell-card]')];
      if (cards.length) {
        cards.forEach((card) => new DawnUpsellCard(card));
        this.setupModal();
        this.setupSlider();
      } else {
        this.loadSameNameProducts();
      }
    }

    async loadSameNameProducts() {
      const name = String(this.dataset.duName || '').trim();
      const message = this.querySelector('[data-du-message]');
      if (!name) { this.hidden = true; return; }
      const root = window.Shopify?.routes?.root || '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      try {
        const qs = new URLSearchParams({
          q: name,
          'resources[type]': 'product',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': 'hide',
          'resources[options][fields]': 'title',
        });
        const response = await fetch(`${base}search/suggest.json?${qs.toString()}`, {
          credentials: 'same-origin', headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Name search returned ' + response.status);
        const payload = await response.json();
        const suggestions = payload.resources?.results?.products || [];
        const isSameName = (title) => {
          const norm = String(title).toLowerCase();
          const word = name.toLowerCase();
          return new RegExp(`(^|[^a-z0-9])${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`, 'i').test(norm);
        };
        const candidates = suggestions.filter((p) =>
          isSameName(p.title) && String(p.id) !== String(this.dataset.duProductId)
        ).slice(0, 10);
        if (!candidates.length) { this.showEmpty(); return; }
        // Small public product JSON responses are used only when Liquid could not
        // resolve the named collection. This preserves real variant IDs and prices.
        const products = await Promise.all(candidates.map(async (p) => {
          const handle = String(p.handle || '');
          if (!handle) return null;
          try {
            const detail = await fetch(`${base}products/${encodeURIComponent(handle)}.js`, {
              credentials: 'same-origin', headers: { Accept: 'application/json' },
            });
            if (!detail.ok) return null;
            return await detail.json();
          } catch { return null; }
        }));
        const list = this.querySelector('[data-du-list]');
        products.filter(Boolean).filter((p) => isSameName(p.title))
          .filter((p) => String(p.id) !== String(this.dataset.duProductId))
          .forEach((p, i) => list.appendChild(this.createSearchCard(p, i)));
        const cards = [...list.querySelectorAll('[data-upsell-card]')];
        if (!cards.length) { this.showEmpty(); return; }
        cards.forEach((card) => new DawnUpsellCard(card));
        this.setupModal();
        this.setupSlider();
      } catch (err) {
        console.warn('[Maliandio name upsell] same-name lookup unavailable', err);
        this.showEmpty();
      }
    }

    showEmpty() {
      if (!window.Shopify?.designMode) { this.hidden = true; return; }
      const msg = this.querySelector('[data-du-message]');
      if (msg) {
        msg.hidden = false;
        msg.textContent = 'No other products matching ' + (this.dataset.duName || 'this name') +
          ' were found. Check the collection or publish additional same-name products.';
      }
    }

    createSearchCard(product, index) {
      const variant = product.variants.find(v => v.available) || product.variants[0];
      const article = document.createElement('article');
      article.className = 'dawn-upsell__card';
      article.dataset.upsellCard = '';
      article.dataset.percentageDiscount = '0';
      article.dataset.fixedDiscount = '0';
      article.dataset.moneyFormat = this.dataset.duMoneyFormat || '${{amount}}';
      article.dataset.addLabel = this.dataset.duAddLabel || 'ADD+';
      article.dataset.soldOutLabel = this.dataset.duSoldOutLabel || 'Sold out';
      article.dataset.skipUnavailable = this.dataset.duSkipUnavailable || 'true';
      article.dataset.productTitle = product.title;
      article.dataset.fallbackImage = product.featured_image || '';
      article.dataset.hasCustomImage = 'false';
      article.dataset.upsellRequiresName = /personalized|custom name|engraved|\[name\]/i.test(product.title) ? 'true' : 'false';
      article.dataset.upsellNameLabel = 'Your name';
      article.dataset.upsellNameProperty = 'Custom name';
      article.dataset.upsellNamePlaceholder = 'Your name here';
      article.dataset.upsellNameMax = '12';
      article.dataset.upsellOptionNames = JSON.stringify(product.options || []);
      const displayImage = this.dataset.duShowImages !== 'false';
      if (displayImage) {
        const media = document.createElement('div');
        media.className = 'dawn-upsell__media';
        const img = document.createElement('img');
        img.className = 'dawn-upsell__image';
        img.alt = product.title;
        img.loading = 'lazy';
        img.src = variant?.featured_image?.src || product.featured_image || '';
        media.appendChild(img);
        article.appendChild(media);
      } else article.classList.add('dawn-upsell__card--no-image');
      const content = document.createElement('div');
      content.className = 'dawn-upsell__content';
      const title = document.createElement('h3');
      title.className = 'dawn-upsell__title';
      title.textContent = product.title;
      content.appendChild(title);
      if (this.dataset.duShowVariants !== 'false' && product.options && product.options.length) {
        const wrapper = document.createElement('div');
        wrapper.className = 'dawn-upsell__variants';
        product.options.forEach((opt, j) => {
          const vals = product.variants.map(v => v.options[j]);
          const distinct = [...new Set(vals)];
          if (distinct.length < 2) return;
          const select = document.createElement('select');
          select.className = 'dawn-upsell__select';
          select.dataset.upsellOption = '';
          select.dataset.optionIndex = String(j);
          select.setAttribute('aria-label', typeof opt === 'string' ? opt : (opt.name || 'Option'));
          distinct.forEach(val => {
            const option = document.createElement('option');
            option.value = val;
            option.textContent = val;
            option.selected = val === variant.options[j];
            select.appendChild(option);
          });
          wrapper.appendChild(select);
        });
        content.appendChild(wrapper);
      }
      const json = document.createElement('script');
      json.type = 'application/json';
      json.dataset.upsellVariants = '';
      json.textContent = JSON.stringify(product.variants);
      content.appendChild(json);
      article.appendChild(content);
      const actions = document.createElement('div');
      actions.className = 'dawn-upsell__actions';
      const prices = document.createElement('div');
      prices.className = 'dawn-upsell__prices';
      const price = document.createElement('span');
      price.className = 'dawn-upsell__price';
      price.dataset.upsellPrice = '';
      price.textContent = formatMoney(Number(variant?.price) || 0, article.dataset.moneyFormat);
      const compare = document.createElement('s');
      compare.className = 'dawn-upsell__compare';
      compare.dataset.upsellCompare = '';
      compare.textContent = formatMoney(Number(variant?.compare_at_price) || 0, article.dataset.moneyFormat);
      prices.append(price, compare);
      content.appendChild(prices);
      const productForm = document.createElement('product-form');
      productForm.className = 'dawn-upsell__product-form';
      const form = document.createElement('form');
      form.className = 'form dawn-upsell__form';
      form.action = `${(window.Shopify?.routes?.root || '/')}cart/add`;
      form.method = 'post';
      const variantId = document.createElement('input');
      variantId.type = 'hidden'; variantId.name = 'id';
      variantId.dataset.upsellVariantId = '';
      variantId.value = variant.id;
      const qty = document.createElement('input');
      qty.type = 'hidden'; qty.name = 'quantity'; qty.value = '1';
      const btn = document.createElement('button');
      btn.type = 'button'; btn.name = 'add'; btn.dataset.upsellOpenModal = '';
      btn.className = 'dawn-upsell__button button button--primary product-form__submit';
      const btnLabel = document.createElement('span');
      btnLabel.dataset.upsellButtonLabel = '';
      btnLabel.textContent = this.dataset.duAddLabel || 'ADD+';
      btn.appendChild(btnLabel);
      const err = document.createElement('div');
      err.className = 'product-form__error-message-wrapper';
      err.setAttribute('role', 'alert');
      err.hidden = true;
      const errSpan = document.createElement('span');
      errSpan.className = 'product-form__error-message';
      err.appendChild(errSpan);
      form.append(variantId, qty, btn);
      productForm.append(err, form);
      actions.appendChild(productForm);
      article.appendChild(actions);
      return article;
    }

    setupModal() {
      if (this._modalSetup) return;
      this.modal = this.querySelector('[data-du-modal]');
      if (!this.modal) return;
      this._modalSetup = true;
      // Portal to document.body: avoids clipping and coordinate bugs caused by
      // transformed/scrolling Product information, sticky ATC, and theme sliders.
      document.body.appendChild(this.modal);
      this.modalDialog = this.modal.querySelector('.dawn-upsell__modal-dialog');
      this.modalImage = this.modal.querySelector('[data-du-modal-image]');
      this.modalTitle = this.modal.querySelector('[data-du-modal-title]');
      this.modalPrice = this.modal.querySelector('[data-du-modal-price]');
      this.modalCompare = this.modal.querySelector('[data-du-modal-compare]');
      this.modalStock = this.modal.querySelector('[data-du-modal-stock]');
      this.modalOptions = this.modal.querySelector('[data-du-modal-options]');
      this.modalNameWrap = this.modal.querySelector('[data-du-modal-name-wrap]');
      this.modalNameLabel = this.modal.querySelector('[data-du-modal-name-label]');
      this.modalNameInput = this.modal.querySelector('[data-du-modal-name-input]');
      this.modalNameCounter = this.modal.querySelector('[data-du-modal-name-counter]');
      this.modalError = this.modal.querySelector('[data-du-modal-error]');
      this.modalSubmit = this.modal.querySelector('[data-du-modal-submit]');
      this.activeCardInstance = null;
      this.activeModalVariant = null;
      this.currentFocusReturn = null;

      // Event delegation also works for products loaded through the search fallback.
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-upsell-open-modal]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        const card = button.closest('[data-upsell-card]');
        const instance = card?.__upsellCardInstance;
        if (instance) this.openModal(instance, button);
      });

      // Click/tap anywhere outside the white panel closes the window.
      this.modal.addEventListener('click', (event) => {
        if (!this.modalDialog.contains(event.target)) this.closeModal();
      });
      this.modal.querySelectorAll('[data-du-modal-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });
      // Native dialog top layer handles theme sticky layers and full viewport overlay.
      this.modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      });
      this._onModalKeydown = (event) => {
        if (this.modal.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal();
        }
      };
      document.addEventListener('keydown', this._onModalKeydown);
      this.modalNameInput?.addEventListener('input', () => {
        if (!this.modalNameCounter) return;
        const max = Number(this.modalNameInput.getAttribute('maxlength')) || 12;
        this.modalNameCounter.textContent = `${this.modalNameInput.value.length}/${max}`;
      });
      this.modalSubmit?.addEventListener('click', () => this.handleModalSubmit());
    }

    openModal(cardInstance, opener) {
      this.activeCardInstance = cardInstance;
      this.currentFocusReturn = opener || document.activeElement;
      this.modalOptions.innerHTML = '';
      this.modalError.hidden = true;
      this.modalError.textContent = '';
      this.modalSubmit.disabled = false;
      const card = cardInstance.card;
      const variant = cardInstance.getCurrentVariant() || cardInstance.variants.find((item) => item.available) || cardInstance.variants[0];
      this.activeModalVariant = variant;
      const title = card.dataset.productTitle || '';
      const image = card.querySelector('.dawn-upsell__image')?.getAttribute('src') || card.dataset.fallbackImage || '';
      if (this.modalImage) {
        if (image) {
          this.modalImage.src = image;
          this.modalImage.alt = title;
          this.modalImage.parentElement.hidden = false;
        } else {
          this.modalImage.parentElement.hidden = true;
        }
      }
      if (this.modalTitle) this.modalTitle.textContent = title;
      const requiresName = card.dataset.upsellRequiresName === 'true';
      const nameLabel = card.dataset.upsellNameLabel || 'Your name';
      const namePlaceholder = card.dataset.upsellNamePlaceholder || 'Your name here';
      const nameMax = Number(card.dataset.upsellNameMax) || 12;
      if (this.modalNameWrap) this.modalNameWrap.classList.toggle('hidden', !requiresName);
      if (this.modalNameLabel) this.modalNameLabel.textContent = nameLabel;
      if (this.modalNameInput) {
        this.modalNameInput.value = '';
        this.modalNameInput.placeholder = namePlaceholder;
        this.modalNameInput.setAttribute('maxlength', String(nameMax));
      }
      if (this.modalNameCounter) this.modalNameCounter.textContent = `0/${nameMax}`;

      (cardInstance.getOptionDefinitions() || []).forEach((definition, index) => {
        const field = document.createElement('div');
        field.className = 'dawn-upsell__modal-field';
        if (/^size\s*_?$/i.test(String(definition.name).trim())) field.classList.add('dawn-upsell__modal-field--size');
        const label = document.createElement('label');
        label.className = 'dawn-upsell__modal-label';
        label.textContent = definition.name;
        const select = document.createElement('select');
        select.className = 'dawn-upsell__modal-select';
        select.dataset.modalOptionIndex = String(index);
        definition.values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = value === (variant?.options?.[index] || cardInstance.selects[index]?.value);
          select.appendChild(option);
        });
        select.addEventListener('change', () => this.onModalOptionChange(index));
        field.appendChild(label);
        field.appendChild(select);
        this.modalOptions.appendChild(field);
      });

      this.updateModalFromVariant(variant, true);
      if (this._modalCloseTimer) clearTimeout(this._modalCloseTimer);
      this.modal.classList.remove('is-open');
      this.modal.hidden = false;
      if (typeof this.modal.showModal === 'function' && !this.modal.open) {
        this.modal.showModal();
      }
      this.modalDialog.scrollTop = 0;
      this.modal.querySelector('.dawn-upsell__modal-body').scrollTop = 0;
      this.lockPageScroll();
      // A separate paint is required for the bottom-sheet slide-up transition.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.modal.hidden) this.modal.classList.add('is-open');
        // Focus close button rather than a select or text input: prevents
        // auto-scroll and opening the soft keyboard as the popup appears.
        this.modal.querySelector('.dawn-upsell__modal-close')?.focus({ preventScroll: true });
      }));
    }

    lockPageScroll() {
      if (this._pageScrollState) return;
      // Desktop: the product gallery uses position:sticky. Never alter the
      // root/body overflow on PC: doing so changes the sticky scroll container
      // and can make the main product image vanish as the dialog opens.
      // The native showModal() dialog already prevents interaction behind it.
      if (window.matchMedia('(min-width: 750px)').matches) {
        this._pageScrollState = { desktop: true };
        return;
      }
      const body = document.body;
      const html = document.documentElement;
      this._pageScrollState = {
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        htmlOverflow: html.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight,
      };
      // Hiding overflow instead of fixing <body> avoids resetting scrollY
      // and prevents sticky product sections from visibly jumping.
      const gap = Math.max(0, window.innerWidth - html.clientWidth);
      if (gap > 0) {
        const currentPadding = parseFloat(getComputedStyle(body).paddingRight) || 0;
        body.style.paddingRight = `${currentPadding + gap}px`;
      }
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.classList.add('dawn-upsell-modal-open');
    }

    unlockPageScroll() {
      if (!this._pageScrollState) return;
      const previous = this._pageScrollState;
      this._pageScrollState = null;
      if (previous.desktop) return;
      const body = document.body;
      document.documentElement.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.paddingRight = previous.bodyPaddingRight;
      body.classList.remove('dawn-upsell-modal-open');
      // Only restore if a mobile browser has unexpectedly moved the viewport.
      if (Math.abs(window.scrollY - previous.scrollY) > 2 ||
          Math.abs(window.scrollX - previous.scrollX) > 2) {
        const docStyle = document.documentElement.style;
        const originalBehavior = docStyle.scrollBehavior;
        docStyle.scrollBehavior = 'auto';
        window.scrollTo(previous.scrollX, previous.scrollY);
        docStyle.scrollBehavior = originalBehavior;
      }
    }

    closeModal(immediate = false) {
      if (!this.modal || this.modal.hidden) return;
      this.modal.classList.remove('is-open');
      if (this._modalCloseTimer) clearTimeout(this._modalCloseTimer);
      const finish = () => {
        if (this.modal.open && typeof this.modal.close === 'function') this.modal.close();
        this.modal.hidden = true;
        this.unlockPageScroll();
        this.activeCardInstance = null;
        this.activeModalVariant = null;
        this._modalCloseTimer = null;
        this.currentFocusReturn?.focus?.({ preventScroll: true });
      };
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        this._modalCloseTimer = window.setTimeout(finish, 240);
      }
    }

    getModalSelectedOptions() {
      return [...this.modal.querySelectorAll('[data-modal-option-index]')].map((select) => select.value);
    }

    onModalOptionChange(changedIndex) {
      if (!this.activeCardInstance) return;
      let variant = this.activeCardInstance.findExactVariant(this.getModalSelectedOptions());
      if (this.activeCardInstance.skipUnavailable && (!variant || !variant.available)) {
        variant = this.activeCardInstance.findBestAvailableVariant(this.getModalSelectedOptions(), changedIndex);
      }
      if (variant) this.updateModalSelectsToVariant(variant);
      this.updateModalFromVariant(variant);
    }

    updateModalSelectsToVariant(variant) {
      this.modal.querySelectorAll('[data-modal-option-index]').forEach((select, index) => {
        const value = variant?.options?.[index];
        if (value !== undefined) select.value = value;
      });
    }

    updateModalFromVariant(variant, isInitial = false) {
      this.activeModalVariant = variant;
      if (!variant) {
        this.modalSubmit.disabled = true;
        return;
      }
      this.modalSubmit.disabled = !variant.available;
      const { price, compare } = this.activeCardInstance.getDisplayPrices(variant);
      if (this.modalPrice) this.modalPrice.textContent = formatMoney(price, this.dataset.duMoneyFormat || '${{amount}}');
      if (this.modalCompare) {
        if (compare > price) {
          this.modalCompare.textContent = formatMoney(compare, this.dataset.duMoneyFormat || '${{amount}}');
          this.modalCompare.classList.remove('hidden');
        } else {
          this.modalCompare.textContent = '';
          this.modalCompare.classList.add('hidden');
        }
      }
      if (this.modalStock) this.modalStock.textContent = variant.available ? 'In stock' : 'Sold out';
      const variantImage = variant.featured_image?.src || variant.featured_media?.preview_image?.src || this.activeCardInstance.fallbackImage;
      if (variantImage && this.modalImage) this.modalImage.src = variantImage;
      this.activeCardInstance.applyVariant(variant, isInitial);
    }

    async handleModalSubmit() {
      if (!this.activeCardInstance || !this.activeModalVariant) return;
      this.modalError.hidden = true;
      this.modalError.textContent = '';
      this.modalSubmit.disabled = true;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', String(this.activeModalVariant.id));
      formData.append('quantity', '1');
      const card = this.activeCardInstance.card;
      const requiresName = card.dataset.upsellRequiresName === 'true';
      const propertyName = card.dataset.upsellNameProperty || 'Custom name';
      if (requiresName && this.modalNameInput && this.modalNameInput.value.trim()) {
        formData.append(`properties[${propertyName}]`, this.modalNameInput.value.trim());
      }
      try {
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        let sectionIds = [];
        if (cart?.getSectionsToRender) {
          sectionIds = cart.getSectionsToRender().map((section) => section.id);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
        }
        delete config.headers['Content-Type'];
        config.body = formData;
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, config);
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message || 'Unable to add this item.');
        if (cart?.renderContents) cart.renderContents(data);
        document.dispatchEvent(new CustomEvent('upsell:item-added', { detail: data }));
        this.closeModal();
      } catch (error) {
        this.modalError.textContent = error?.message || 'Something went wrong. Please try again.';
        this.modalError.hidden = false;
      } finally {
        if (!this.modal.hidden) this.modalSubmit.disabled = false;
      }
    }

    setupSlider() {
      const list = this.querySelector('[data-du-list]');
      if (!list) return;
      const cards = [...list.querySelectorAll('[data-upsell-card]')];
      if (!cards.length) return;
      // Group three cards per page; all three rows stay in the layout during sliding.
      const pages = [];
      for (let i = 0; i < cards.length; i += 3) {
        const page = document.createElement('div');
        page.className = 'dawn-upsell__page';
        cards.slice(i, i + 3).forEach(card => page.appendChild(card));
        pages.push(page);
      }
      list.replaceChildren(...pages);
      this.currentPage = 0;
      const arrows = this.querySelector('[data-du-arrows]');
      const dots = this.querySelector('[data-du-dots]');
      const prev = this.querySelector('[data-du-prev]');
      const next = this.querySelector('[data-du-next]');
      if (arrows) arrows.hidden = pages.length <= 1;
      if (dots) dots.hidden = pages.length <= 1;
      dots?.replaceChildren();
      pages.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dawn-upsell__dot';
        dot.setAttribute('aria-label', 'Show group ' + (i + 1));
        dot.addEventListener('click', () => this.moveTo(i));
        dots?.appendChild(dot);
      });
      prev?.addEventListener('click', () => this.moveTo(this.currentPage - 1));
      next?.addEventListener('click', () => this.moveTo(this.currentPage + 1));
      this.pageCount = pages.length;
      this.moveTo(0, true);
      this.attachSwipe();
      this.classList.add('dawn-upsell--ready');
    }

    moveTo(page, instant = false) {
      if (!this.pageCount) return;
      const index = Math.min(this.pageCount - 1, Math.max(0, page));
      this.currentPage = index;
      const list = this.querySelector('[data-du-list]');
      if (list) {
        list.style.transitionDuration = instant ? '0ms' : '';
        list.style.transform = `translate3d(-${index * 100}%,0,0)`;
      }
      this.querySelector('[data-du-prev]')?.toggleAttribute('disabled', index === 0);
      this.querySelector('[data-du-next]')?.toggleAttribute('disabled', index === this.pageCount - 1);
      this.querySelectorAll('.dawn-upsell__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    attachSwipe() {
      const viewport = this.querySelector('[data-du-viewport]');
      if (!viewport || this.pageCount <= 1) return;
      let startX = 0, startY = 0, deltaX = 0, direction = null;
      viewport.addEventListener('touchstart', e => {
        if (e.touches.length !== 1 || e.target.closest('select,button,input,a')) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        deltaX = 0;
        direction = null;
      }, {passive:true});
      viewport.addEventListener('touchmove', e => {
        if (e.touches.length !== 1 || !startX) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;
        if (!direction && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
          direction = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'x' : 'y';
        }
        if (direction === 'x') {
          deltaX = dx;
          const list = this.querySelector('[data-du-list]');
          if (list) {
            list.style.transitionDuration = '0ms';
            const width = viewport.clientWidth || 1;
            list.style.transform = `translate3d(calc(-${this.currentPage * 100}% + ${dx / width * 100}%),0,0)`;
          }
        }
      }, {passive:true});
      viewport.addEventListener('touchend', () => {
        if (!startX) return;
        const list = this.querySelector('[data-du-list]');
        if (list) list.style.transitionDuration = '';
        const next = direction === 'x' && Math.abs(deltaX) > 42
          ? this.currentPage + (deltaX < 0 ? 1 : -1)
          : this.currentPage;
        startX = 0; direction = null; deltaX = 0;
        this.moveTo(next);
      }, {passive:true});
      viewport.addEventListener('touchcancel', () => {
        startX = 0; deltaX = 0; direction = null;
        const list = this.querySelector('[data-du-list]');
        if (list) list.style.transitionDuration = '';
        this.moveTo(this.currentPage);
      }, {passive:true});
    }
  }


  class DawnUpsellCard {
    constructor(card) {
      this.card = card;
      if (card.dataset.duCardInitialized === 'true') return;
      card.dataset.duCardInitialized = 'true';

      this.variantScript = card.querySelector('[data-upsell-variants]');
      this.selects = [...card.querySelectorAll('[data-upsell-option]')];
      this.variantInput = card.querySelector('[data-upsell-variant-id]');
      this.priceElement = card.querySelector('[data-upsell-price]');
      this.compareElement = card.querySelector('[data-upsell-compare]');
      this.image = card.querySelector('.dawn-upsell__image');
      this.button = card.querySelector('.dawn-upsell__button');
      this.buttonLabel = card.querySelector('[data-upsell-button-label]');

      this.percentageDiscount = Math.min(
        100,
        Math.max(0, Number.parseFloat(card.dataset.percentageDiscount) || 0)
      );
      this.fixedDiscount = Math.max(0, Number.parseInt(card.dataset.fixedDiscount, 10) || 0);
      this.moneyFormat = card.dataset.moneyFormat || '${{amount}}';
      this.addLabel = card.dataset.addLabel || 'Add+';
      this.soldOutLabel = card.dataset.soldOutLabel || 'Sold out';
      this.skipUnavailable = parseBoolean(card.dataset.skipUnavailable);
      this.fallbackImage = card.dataset.fallbackImage || '';
      this.hasCustomImage = parseBoolean(card.dataset.hasCustomImage);
      this.productTitle = card.dataset.productTitle || '';

      try {
        this.variants = JSON.parse(this.variantScript?.textContent || '[]');
      } catch (error) {
        console.error('[Dawn product upsell] Invalid variants JSON.', error);
        this.variants = [];
      }

      if (!this.variants.length || !this.variantInput || !this.button) return;

      this.selects.forEach((select) => {
        select.addEventListener('change', (event) => this.onOptionChange(event));
      });

      const initialVariant =
        this.variants.find((variant) => String(variant.id) === String(this.variantInput.value)) ||
        this.variants.find((variant) => variant.available) ||
        this.variants[0];

      if (initialVariant) {
        this.applyVariant(initialVariant, true);
      }
      this.card.__upsellCardInstance = this;
    }

    getCurrentVariant() {
      return this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value));
    }

    getOptionDefinitions() {
      let productOptionNames = [];
      try { productOptionNames = JSON.parse(this.card.dataset.upsellOptionNames || '[]'); } catch(e) {}
      const optionCount = Math.max(
        this.selects.length,
        Array.isArray(productOptionNames) ? productOptionNames.length : 0,
        this.variants[0]?.options?.length || 0
      );
      return Array.from({length: optionCount}, (_, index) => {
        const optionName = productOptionNames[index];
        const select = this.selects[index];
        const values = select
          ? [...select.options].map((option) => option.value)
          : [...new Set(this.variants.map((variant) => variant.options?.[index]).filter(Boolean))];
        return {
          name: select?.getAttribute('aria-label') ||
            (typeof optionName === 'string' ? optionName : optionName?.name) ||
            `Option ${index + 1}`,
          values
        };
      }).filter(({name, values}) => values.length && !(values.length === 1 && name.toLowerCase() === 'title' && values[0] === 'Default Title'));
    }

    getDisplayPrices(variant) {
      const basePrice = Number(variant?.price) || 0;
      const price = Math.max(0, Math.round(basePrice * ((100 - this.percentageDiscount) / 100) - this.fixedDiscount));
      const compareAt = Number(variant?.compare_at_price) || 0;
      const compare = compareAt > price ? compareAt : basePrice > price ? basePrice : 0;
      return { price, compare };
    }

    getSelectedOptions() {
      return this.selects.map((select) => select.value);
    }

    findExactVariant(options) {
      return this.variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === options.length &&
          variant.options.every((value, index) => value === options[index])
      );
    }

    findBestAvailableVariant(options, changedIndex) {
      const availableVariants = this.variants.filter((variant) => variant.available);
      if (!availableVariants.length) return null;

      const matchingChangedValue = availableVariants.filter(
        (variant) => variant.options?.[changedIndex] === options[changedIndex]
      );
      const candidates = matchingChangedValue.length ? matchingChangedValue : availableVariants;

      return candidates
        .map((variant) => {
          const score = variant.options.reduce(
            (total, value, index) => total + (value === options[index] ? 1 : 0),
            0
          );
          return { variant, score };
        })
        .sort((a, b) => b.score - a.score)[0].variant;
    }

    onOptionChange(event) {
      const changedIndex = Number.parseInt(event.currentTarget.dataset.optionIndex, 10) || 0;
      const selectedOptions = this.getSelectedOptions();
      let variant = this.findExactVariant(selectedOptions);

      if (this.skipUnavailable && (!variant || !variant.available)) {
        variant = this.findBestAvailableVariant(selectedOptions, changedIndex);
        if (variant) this.syncSelectsToVariant(variant);
      }

      this.applyVariant(variant);
    }

    syncSelectsToVariant(variant) {
      this.selects.forEach((select, index) => {
        const value = variant.options?.[index];
        if (value !== undefined) select.value = value;
      });
    }

    updateOptionAvailability() {
      if (!this.skipUnavailable || !this.selects.length) return;

      const selectedOptions = this.getSelectedOptions();

      this.selects.forEach((select, optionIndex) => {
        [...select.options].forEach((option) => {
          const isAvailable = this.variants.some((variant) => {
            if (!variant.available || variant.options?.[optionIndex] !== option.value) return false;

            for (let previousIndex = 0; previousIndex < optionIndex; previousIndex += 1) {
              if (variant.options?.[previousIndex] !== selectedOptions[previousIndex]) return false;
            }

            return true;
          });

          option.disabled = !isAvailable;
        });

        if (select.selectedOptions[0]?.disabled) {
          const firstEnabled = [...select.options].find((option) => !option.disabled);
          if (firstEnabled) select.value = firstEnabled.value;
        }
      });
    }

    applyVariant(variant, isInitial = false) {
      this.updateOptionAvailability();

      if (!variant) {
        this.variantInput.value = '';
        this.setButtonState(false);
        return;
      }

      if (this.skipUnavailable && !variant.available) {
        const fallback = this.variants.find((item) => item.available);
        if (fallback) {
          this.syncSelectsToVariant(fallback);
          variant = fallback;
          this.updateOptionAvailability();
        }
      }

      this.variantInput.value = variant.id;
      this.setButtonState(Boolean(variant.available));
      this.updatePrice(variant);
      this.updateImage(variant, isInitial);
    }

    setButtonState(isAvailable) {
      this.button.disabled = !isAvailable;
      this.button.setAttribute('aria-disabled', String(!isAvailable));

      if (this.buttonLabel) {
        this.buttonLabel.textContent = isAvailable ? this.addLabel : this.soldOutLabel;
      }
    }

    updatePrice(variant) {
      const { price: visualPrice, compare: visualCompare } = this.getDisplayPrices(variant);

      if (this.priceElement) {
        this.priceElement.textContent = formatMoney(visualPrice, this.moneyFormat);
      }

      if (this.compareElement) {
        if (visualCompare > visualPrice) {
          this.compareElement.textContent = formatMoney(visualCompare, this.moneyFormat);
          this.compareElement.classList.remove('hidden');
        } else {
          this.compareElement.textContent = '';
          this.compareElement.classList.add('hidden');
        }
      }
    }

    updateImage(variant, isInitial) {
      if (!this.image) return;

      const variantImage = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
      const nextImage = variantImage || this.fallbackImage;

      if (!nextImage) return;
      if (isInitial && this.hasCustomImage && this.image.getAttribute('src')) return;
      if (isInitial && !variantImage && this.image.getAttribute('src')) return;

      this.image.removeAttribute('srcset');
      this.image.removeAttribute('sizes');
      this.image.src = nextImage;
      this.image.alt =
        variant.featured_image?.alt ||
        variant.featured_media?.alt ||
        this.productTitle ||
        this.image.alt ||
        '';
    }
  }

  if (!customElements.get(SELECTOR)) {
    customElements.define(SELECTOR, DawnProductUpsell);
  }
})();
